// S-2210: Result assembler for merging chunked analysis results
import { ChunkedAnalysisResult, PartialAnalysisResult, AnalysisResult } from './openai-client';

export interface AssemblyConfig {
  preservePartInfo: boolean;
  weightByFrameCount: boolean;
  minValidParts: number;
}

export interface AssemblyReport {
  runId: string;
  totalParts: number;
  validParts: number;
  failedParts: number;
  framesCovered: number;
  framesAnalyzed: number;
  coverageRatio: number;
  qualityScore: number;
}

export class ResultAssembler {
  private config: AssemblyConfig;

  constructor(config: AssemblyConfig = {
    preservePartInfo: false,
    weightByFrameCount: true,
    minValidParts: 1
  }) {
    this.config = config;
  }

  /**
   * S-2210: Assemble chunked results into a single analysis
   * Maintains frame order and provides quality metrics
   */
  assembleResults(chunkedResult: ChunkedAnalysisResult): {
    analysis: AnalysisResult;
    report: AssemblyReport;
  } {
    console.log(`[S-2210 Assembler] Assembling ${chunkedResult.results.length} parts for runId: ${chunkedResult.runId}`);
    
    const validResults = chunkedResult.results.filter(r => r.frameCount > 0);
    const failedResults = chunkedResult.results.filter(r => r.frameCount === 0);
    
    if (validResults.length < this.config.minValidParts) {
      throw new Error(`Insufficient valid parts: ${validResults.length} < ${this.config.minValidParts}`);
    }
    
    // Sort results by part index to maintain frame order
    const sortedResults = validResults.sort((a, b) => a.partIndex - b.partIndex);
    
    // Assemble bullets
    const assembledBullets = this.assembleBullets(sortedResults);
    
    // Assemble scores
    const assembledScores = this.assembleScores(sortedResults);
    
    // Create final analysis result
    const analysis: AnalysisResult = {
      bullets: assembledBullets,
      scores: assembledScores,
      frameAudit: chunkedResult.frameAudit
    };
    
    // Generate assembly report
    const report = this.generateReport(chunkedResult, validResults, failedResults);
    
    console.log(`[S-2210 Assembler] Assembly complete: ${assembledBullets.length} bullets, quality=${report.qualityScore}`);
    
    return { analysis, report };
  }

  private assembleBullets(results: PartialAnalysisResult[]): string[] {
    const bullets: string[] = [];
    
    // Add frame coverage summary
    const totalFrames = results.reduce((sum, r) => sum + r.frameCount, 0);
    const frameRanges = results.map(r => {
      const indices = r.frameIndices;
      return indices.length > 0 ? `${indices[0]}-${indices[indices.length - 1]}` : 'none';
    });
    
    bullets.push(`Analysis covers ${totalFrames} frames across segments: ${frameRanges.join(', ')}`);
    
    // Combine bullets from all parts
    for (const result of results) {
      if (this.config.preservePartInfo) {
        // Add part-specific bullets with segment info
        const partBullets = result.bullets.map(bullet => 
          `[Frames ${result.frameIndices[0]}-${result.frameIndices[result.frameIndices.length - 1]}] ${bullet}`
        );
        bullets.push(...partBullets);
      } else {
        // Merge bullets without part info for cleaner output
        bullets.push(...result.bullets);
      }
    }
    
    // Remove duplicates and empty bullets
    const uniqueBullets = Array.from(new Set(bullets.filter(b => b.trim().length > 0)));
    
    return uniqueBullets;
  }

  private assembleScores(results: PartialAnalysisResult[]): {
    overall: number;
    tempo: number;
    form: number;
    power: number;
  } {
    if (results.length === 0) {
      return { overall: 0, tempo: 0, form: 0, power: 0 };
    }
    
    if (results.length === 1) {
      return results[0].scores;
    }
    
    // Calculate weighted average if enabled
    if (this.config.weightByFrameCount) {
      return this.calculateWeightedScores(results);
    } else {
      return this.calculateSimpleAverageScores(results);
    }
  }

  private calculateWeightedScores(results: PartialAnalysisResult[]): {
    overall: number;
    tempo: number;
    form: number;
    power: number;
  } {
    let totalWeight = 0;
    let weightedOverall = 0;
    let weightedTempo = 0;
    let weightedForm = 0;
    let weightedPower = 0;
    
    for (const result of results) {
      const weight = result.frameCount;
      totalWeight += weight;
      
      weightedOverall += result.scores.overall * weight;
      weightedTempo += result.scores.tempo * weight;
      weightedForm += result.scores.form * weight;
      weightedPower += result.scores.power * weight;
    }
    
    if (totalWeight === 0) {
      return { overall: 0, tempo: 0, form: 0, power: 0 };
    }
    
    return {
      overall: Math.round(weightedOverall / totalWeight),
      tempo: Math.round(weightedTempo / totalWeight),
      form: Math.round(weightedForm / totalWeight),
      power: Math.round(weightedPower / totalWeight)
    };
  }

  private calculateSimpleAverageScores(results: PartialAnalysisResult[]): {
    overall: number;
    tempo: number;
    form: number;
    power: number;
  } {
    const count = results.length;
    
    const sumOverall = results.reduce((sum, r) => sum + r.scores.overall, 0);
    const sumTempo = results.reduce((sum, r) => sum + r.scores.tempo, 0);
    const sumForm = results.reduce((sum, r) => sum + r.scores.form, 0);
    const sumPower = results.reduce((sum, r) => sum + r.scores.power, 0);
    
    return {
      overall: Math.round(sumOverall / count),
      tempo: Math.round(sumTempo / count),
      form: Math.round(sumForm / count),
      power: Math.round(sumPower / count)
    };
  }

  private generateReport(
    chunkedResult: ChunkedAnalysisResult,
    validResults: PartialAnalysisResult[],
    failedResults: PartialAnalysisResult[]
  ): AssemblyReport {
    const framesCovered = validResults.reduce((sum, r) => sum + r.frameCount, 0);
    const framesAnalyzed = chunkedResult.frameAudit.framesAnalyzed;
    const coverageRatio = chunkedResult.frameAudit.totalFrames > 0 
      ? framesCovered / chunkedResult.frameAudit.totalFrames 
      : 0;
    
    // Calculate quality score based on coverage and success rate
    const successRate = validResults.length / chunkedResult.totalParts;
    const qualityScore = Math.round((coverageRatio * 0.7 + successRate * 0.3) * 100);
    
    return {
      runId: chunkedResult.runId,
      totalParts: chunkedResult.totalParts,
      validParts: validResults.length,
      failedParts: failedResults.length,
      framesCovered,
      framesAnalyzed,
      coverageRatio,
      qualityScore
    };
  }
}

export const defaultAssembler = new ResultAssembler();
